import { useCallback, useRef } from "react";

import { applyPartialReplacement, isPartialRangeIntact } from "./partials";
import type { LiveSegment } from "./segments";

export type TextInsertionOptions = {
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  isSelection?: boolean;
  selectionStart?: number;
  selectionEnd?: number;
  /**
   * Speaker label from Azure AI Speech diarization ("Guest-1" / "Guest-2" /
   * … or "Unknown" for an utterance too short to confidently attribute —
   * see segments.ts's isUnattributedSpeaker) when the session was started
   * with speaker diarization enabled. Undefined for single-speaker
   * sessions. Consumers that build a segmented_full_transcript can group
   * on this; plain-textarea consumers can ignore it.
   */
  speaker?: string;
};

export type TranscriptionCallback = {
  onFocus?: () => void; // Called when transcription starts (to focus target)
  onEnd?: () => void; // Called when transcription ends
  onTextInsert: (text: string, options: TextInsertionOptions) => void; // Called with transcribed text
  // Called as Azure emits partial (mid-turn) hypotheses, before the
  // text is finalized via onTextInsert. Empty string when a turn ends or
  // there's no in-flight partial. Optional — callers that only want the
  // finalized text can ignore it.
  onPartialUpdate?: (partial: string) => void;
  // Called on each finalized turn when the session has speaker diarization
  // enabled, with the full speaker-grouped segment list so far, and the
  // session's configured max speaker count (the same value passed to
  // startTranscription's StreamingOptions). Consumers can render a live
  // speaker-attributed view and/or persist it as segmented_full_transcript;
  // the max speaker count is there so a role-mapping heuristic can cap how
  // many distinct roles it invents. Omitted for single-speaker sessions.
  onSegmentsUpdate?: (segments: LiveSegment[], maxSpeakers?: number) => void;
};

/**
 * A hook that creates handlers for managing transcription text insertion in textareas
 *
 * @param textareaRef Reference to the textarea element
 * @param setValue State setter for the textarea value
 * @param onChange Optional callback for when the value changes
 * @returns TranscriptionCallback object with handlers for focus, end, and text insertion
 *
 * @example
 * ```tsx
 * const textareaRef = useRef<HTMLTextAreaElement>(null);
 * const [value, setValue] = useState('');
 * const transcriptionHandler = useTextareaTranscriptionHandler(
 *   textareaRef,
 *   setValue,
 *   (newValue) => console.log('Value changed:', newValue)
 * );
 *
 * // Then use with Transcriber component
 * <Transcriber callback={transcriptionHandler} />
 * ```
 */
export const useTextareaTranscriptionHandler = (
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  setValue: React.Dispatch<React.SetStateAction<string>>,
  onChange?: (value: string) => void
) => {
  // Store the last cursor position for continuous transcription
  const lastCursorPosRef = useRef<number | null>(null);

  // In-flight partial-transcript range tracking. AssemblyAI streams
  // partial Turn events as the doctor talks; we write each one directly
  // into the textarea and replace it in place as the hypothesis is
  // refined, then commit on end_of_turn (onTextInsert). Without this,
  // text only appears in bursts at turn boundaries.
  const partialStartRef = useRef<number | null>(null);
  const partialLengthRef = useRef<number>(0);
  const lastPartialTextRef = useRef<string>("");

  const resetPartialRange = useCallback(() => {
    partialStartRef.current = null;
    partialLengthRef.current = 0;
    lastPartialTextRef.current = "";
  }, []);

  const onFocus = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      lastCursorPosRef.current = textareaRef.current.selectionStart;
    }
  }, [textareaRef]);

  const onEnd = useCallback(() => {
    // Reset cursor + partial-range tracking at end of session
    lastCursorPosRef.current = null;
    resetPartialRange();
  }, [resetPartialRange]);

  const onPartialUpdate = useCallback(
    (partial: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Empty partial is the Transcriber's "end of partial" hint — the
      // final text has either been committed via onTextInsert or the
      // session is closing. Just close the range.
      if (partial === "") {
        resetPartialRange();
        return;
      }

      // First partial of a fresh turn: anchor at the current cursor.
      // Refocus first in case the silence modal just closed and stole
      // focus — an unfocused textarea reports selectionStart === 0,
      // which would anchor the next partial at the very start of the
      // field instead of where the doctor was dictating.
      if (partialStartRef.current === null) {
        if (document.activeElement !== textarea) {
          textarea.focus();
        }
        partialStartRef.current = textarea.selectionStart;
        partialLengthRef.current = 0;
        lastPartialTextRef.current = "";
      }

      const start = partialStartRef.current;
      const oldLength = partialLengthRef.current;
      const current = textarea.value;

      // If the user has typed into our partial range, surrender it — the
      // next partial will start a fresh range at the new cursor.
      if (
        !isPartialRangeIntact({
          current,
          start,
          oldLength,
          lastPartial: lastPartialTextRef.current,
        })
      ) {
        resetPartialRange();
        return;
      }

      const { text: nextValue, newLength } = applyPartialReplacement({
        current,
        start,
        oldLength,
        partial,
      });

      setValue(() => {
        onChange?.(nextValue);
        return nextValue;
      });
      partialLengthRef.current = newLength;
      lastPartialTextRef.current = partial;

      // Restore cursor at end of partial after React commits.
      const cursor = start + newLength;
      lastCursorPosRef.current = cursor;
      setTimeout(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.setSelectionRange(cursor, cursor);
      }, 0);
    },
    [textareaRef, setValue, onChange, resetPartialRange]
  );

  const onTextInsert = useCallback(
    (text: string) => {
      const textarea = textareaRef.current;

      // Fast path: an active partial range exists and is still intact —
      // replace it with the finalised text. Skip the cursor-insert
      // branch entirely so we don't double-write the same words.
      if (textarea && partialStartRef.current !== null) {
        const start = partialStartRef.current;
        const oldLength = partialLengthRef.current;
        const current = textarea.value;

        if (
          isPartialRangeIntact({
            current,
            start,
            oldLength,
            lastPartial: lastPartialTextRef.current,
          })
        ) {
          const { text: nextValue, newLength } = applyPartialReplacement({
            current,
            start,
            oldLength,
            partial: text,
          });
          setValue(() => {
            onChange?.(nextValue);
            return nextValue;
          });
          const cursor = start + newLength;
          lastCursorPosRef.current = cursor;
          resetPartialRange();
          setTimeout(() => {
            const ta = textareaRef.current;
            if (!ta) return;
            ta.focus();
            ta.setSelectionRange(cursor, cursor);
          }, 0);
          return;
        }

        // Range invalidated by the user typing — fall through to a
        // plain cursor-insert so we don't clobber their edits.
        resetPartialRange();
      }

      if (textarea) {
        // Always update with current cursor position
        lastCursorPosRef.current = textarea.selectionStart;

        // Use current selection start/end for text insertion
        const startPos = textarea.selectionStart;
        const endPos = textarea.selectionEnd;

        // Check if text is selected (highlighted)
        const hasSelection = startPos !== endPos;

        // Use function form of setState to ensure we're using the most up-to-date state value
        setValue((currentValue) => {
          let newValue;

          if (hasSelection) {
            // Replace only the selected text with the transcription
            newValue =
              currentValue.substring(0, startPos) +
              text +
              currentValue.substring(endPos);
          } else {
            // Insert at cursor position without replacing existing text
            newValue =
              currentValue.substring(0, startPos) +
              text +
              currentValue.substring(startPos);
          }

          // Call onChange with the new value if provided
          onChange?.(newValue);
          return newValue;
        });

        // Update the cursor position reference
        const newCursorPos = startPos + text.length;
        lastCursorPosRef.current = newCursorPos;

        // Set cursor position after the inserted text
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      } else {
        // Fallback to appending the transcribed text if no ref available
        setValue((currentValue) => {
          const newValue = currentValue + text;
          onChange?.(newValue);
          return newValue;
        });
      }
    },
    [textareaRef, setValue, onChange, resetPartialRange]
  );

  return {
    onFocus,
    onEnd,
    onPartialUpdate,
    onTextInsert,
  } as TranscriptionCallback;
};
